import { webrtcBits, type WebRtcExposure } from '../entropy/estimates';
import type { Collector } from './types';
import { blocked, safe, signal } from './util';

export type CandidateKind =
  | 'mdns'
  | 'private-ipv4'
  | 'cgnat-ipv4'
  | 'link-local'
  | 'loopback'
  | 'public-ipv4'
  | 'ipv6-ula'
  | 'public-ipv6'
  | 'unspecified'
  | 'unknown';

export interface ParsedCandidate {
  address: string;
  kind: CandidateKind;
  type: string;
  protocol: string;
}

function ipv4Octets(addr: string): number[] | null {
  const m = /^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/.exec(addr);
  if (!m) return null;
  const octets = m.slice(1).map(Number);
  return octets.every((n) => n <= 255) ? octets : null;
}

export function classifyAddress(raw: string): CandidateKind {
  const addr = raw.trim().replace(/^\[|\]$/g, '').toLowerCase();
  if (!addr) return 'unknown';
  if (addr.endsWith('.local')) return 'mdns';
  const v4 = ipv4Octets(addr);
  if (v4) {
    const [a = 0, b = 0] = v4;
    if (a === 0) return 'unspecified';
    if (a === 127) return 'loopback';
    if (a === 10 || (a === 172 && b >= 16 && b <= 31) || (a === 192 && b === 168)) return 'private-ipv4';
    if (a === 100 && b >= 64 && b <= 127) return 'cgnat-ipv4';
    if (a === 169 && b === 254) return 'link-local';
    return 'public-ipv4';
  }
  if (addr.includes(':')) {
    if (addr === '::') return 'unspecified';
    if (addr === '::1') return 'loopback';
    const first = parseInt(addr.split(':')[0] || '0', 16);
    if ((first & 0xffc0) === 0xfe80) return 'link-local';
    if ((first & 0xfe00) === 0xfc00) return 'ipv6-ula';
    if ((first & 0xe000) === 0x2000) return 'public-ipv6';
    return 'unknown';
  }
  return 'unknown';
}

/** Parse an ICE candidate line ("candidate:… 1 udp 2122260223 192.168.1.5 54321 typ host …"). */
export function parseCandidate(line: string): ParsedCandidate | null {
  const body = line.trim().replace(/^a=/, '').replace(/^candidate:/, '');
  const parts = body.split(/\s+/);
  if (parts.length < 8) return null;
  const typIndex = parts.indexOf('typ');
  const address = parts[4] ?? '';
  return {
    address,
    kind: classifyAddress(address),
    type: typIndex >= 0 ? (parts[typIndex + 1] ?? 'unknown') : 'unknown',
    protocol: (parts[2] ?? '').toLowerCase(),
  };
}

const PUBLIC: CandidateKind[] = ['public-ipv4', 'public-ipv6'];
const PRIVATE: CandidateKind[] = ['private-ipv4', 'cgnat-ipv4', 'link-local', 'loopback', 'ipv6-ula'];

export function summarizeCandidates(candidates: ParsedCandidate[]): { exposure: WebRtcExposure; addresses: string[] } {
  const kinds = new Set(candidates.map((c) => c.kind));
  const addresses = Array.from(new Set(candidates.map((c) => c.address))).sort();
  let exposure: WebRtcExposure = 'none';
  if (PUBLIC.some((k) => kinds.has(k))) exposure = 'public';
  else if (PRIVATE.some((k) => kinds.has(k))) exposure = 'private';
  else if (kinds.has('mdns')) exposure = 'mdns';
  return { exposure, addresses };
}

/**
 * Gather ICE candidates with NO STUN/TURN servers configured. Host candidates are discovered from
 * the local network interfaces only; nothing is sent to a remote host.
 */
async function gatherCandidates(timeoutMs = 2000): Promise<string[]> {
  const pc = new RTCPeerConnection({ iceServers: [] });
  const lines = new Set<string>();
  try {
    pc.createDataChannel('probe');
    const finished = new Promise<void>((resolve) => {
      const timer = setTimeout(resolve, timeoutMs);
      pc.onicecandidate = (e) => {
        if (e.candidate?.candidate) lines.add(e.candidate.candidate);
        else if (!e.candidate) {
          clearTimeout(timer);
          resolve();
        }
      };
      pc.onicegatheringstatechange = () => {
        if (pc.iceGatheringState === 'complete') {
          clearTimeout(timer);
          resolve();
        }
      };
    });
    await pc.setLocalDescription(await pc.createOffer());
    await finished;
    for (const l of (pc.localDescription?.sdp ?? '').split(/\r?\n/)) if (l.startsWith('a=candidate:')) lines.add(l.slice(2));
    return Array.from(lines);
  } finally {
    pc.close();
  }
}

const EXPOSURE_TEXT: Record<WebRtcExposure, string> = {
  public: 'Public IP address exposed',
  private: 'Real local IP address exposed',
  mdns: 'Local IP hidden behind a random .local name',
  none: 'No local candidates exposed',
};

export const webrtcCollector: Collector = {
  id: 'webrtc',
  title: 'WebRTC',
  description:
    'Whether the real-time communication API leaks your local network addresses. Checked with no STUN server, so nothing leaves your device.',
  async collect() {
    const available = typeof RTCPeerConnection === 'function';
    const availability = signal('webrtc.available', 'RTCPeerConnection available', available, {
      display: available ? 'Yes' : 'No (WebRTC disabled or unsupported)',
    });
    if (!available) return [availability, blocked('webrtc.localIps', 'Local IP exposure', 'WebRTC is disabled, so nothing can leak through it')];

    const leak = await safe('webrtc.localIps', 'Local IP exposure', async () => {
      const parsed = (await gatherCandidates()).map(parseCandidate).filter((c): c is ParsedCandidate => !!c);
      const { exposure, addresses } = summarizeCandidates(parsed);
      const value = { exposure, candidates: parsed.map(({ address, kind, type }) => ({ address, kind, type })) };
      const detail = addresses.length ? `\n${parsed.map((c) => `${c.address} (${c.kind}, ${c.type})`).join('\n')}` : '';
      return signal('webrtc.localIps', 'Local IP exposure', value, {
        display: `${EXPOSURE_TEXT[exposure]}${detail}`,
        bits: webrtcBits(exposure),
        note: 'A real tracker would also configure a STUN server to learn your public IP, even through some VPNs.',
      });
    });
    return [availability, leak];
  },
};
